const hasViewrExtension = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return /\.viewr(?:$|[?#])/i.test(value.trim());
};

const extractExplicitPathname = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null;

  try {
    const url = new URL(value);
    if (url.pathname.includes("/o/")) {
      const encodedObjectPath = url.pathname.split("/o/")[1] ?? "";
      return decodeURIComponent(encodedObjectPath);
    }
    return url.pathname ? decodeURIComponent(url.pathname) : null;
  } catch {
    return value;
  }
};

const extractViewrObjectPath = (
  value: string | null | undefined,
): string | null => {
  const explicitPathname = extractExplicitPathname(value);
  if (!explicitPathname) return null;

  const normalized = explicitPathname
    .replace(/^gs:\/\/[^/]+\//, "")
    .replace(/^\/+/, "")
    .trim();

  if (!normalized) {
    return null;
  }

  try {
    return decodeURIComponent(normalized);
  } catch {
    return normalized;
  }
};

const normalizeLogicalProjectBasename = (basename: string): string =>
  basename
    .toLowerCase()
    .replace(/ \(\d+\)(?=\.viewr$)/i, "")
    .trim();

export const resolveLogicalProjectIdentity = (
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    const objectPath = extractViewrObjectPath(candidate);
    if (!objectPath || !hasViewrExtension(objectPath)) {
      continue;
    }

    const segments = objectPath.split("/").filter(Boolean);
    if (segments.length === 0) {
      continue;
    }

    const basename = segments[segments.length - 1];
    const directory = segments.slice(0, -1).join("/").toLowerCase();
    if (directory) {
      return directory;
    }

    return normalizeLogicalProjectBasename(basename);
  }

  return null;
};

export const areLogicalProjectIdentitiesEquivalent = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => {
  const leftIdentity = resolveLogicalProjectIdentity(left);
  const rightIdentity = resolveLogicalProjectIdentity(right);

  if (!leftIdentity || !rightIdentity) {
    return false;
  }

  return leftIdentity === rightIdentity;
};
